import { useRef, useLayoutEffect, useState, type ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { FS_XS, COLOR_PRIMARY, FS_SM } from '../common/theme'
import type { TabHeaderItem } from '../types'

interface TabHeaderProps<T extends string = string> {
  tabs: TabHeaderItem<T>[]
  active: T
  onChange: (key: T) => void
  endSlot?: ReactNode
  testIdPrefix?: string
  size?: 'small' | 'large'
  fullWidth?: boolean
  // Allow tabs to wrap onto multiple rows when the container is narrow. When
  // wrapping, the sliding underline indicator is replaced by a per-tab bottom
  // border (the absolute indicator can't span multiple rows correctly).
  wrap?: boolean
}

export default function TabHeader<T extends string = string>({
  tabs, active, onChange, endSlot, testIdPrefix, size = 'small', fullWidth = false, wrap = false,
}: TabHeaderProps<T>) {
  const isLarge = size === 'large'
  const tabRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false })

  useLayoutEffect(() => {
    const el = tabRefs.current.get(active)
    if (el) {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth, ready: true })
    }
  }, [active, tabs])

  return (
    <Box
      data-testid={testIdPrefix ? `${testIdPrefix}_header` : undefined}
      sx={{
        position: 'relative',
        display: 'flex', alignItems: 'center', flexShrink: 0,
        ...(wrap ? { flexWrap: 'wrap' } : {}),
        borderBottom: 1, borderColor: 'divider',
        px: 0.5, userSelect: 'none',
      }}
    >
      {tabs.map(tab => {
        const isActive = tab.key === active
        return (
          <Box
            key={tab.key}
            ref={(el: HTMLElement | null) => {
              if (el) tabRefs.current.set(tab.key, el)
              else tabRefs.current.delete(tab.key)
            }}
            data-testid={testIdPrefix ? `${testIdPrefix}_tab_${tab.key}` : undefined}
            data-tab-active={isActive ? 'true' : 'false'}
            onClick={() => onChange(tab.key)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25,
              ...(fullWidth ? { flex: 1 } : {}),
              px: isLarge ? 1.5 : 1.25, py: isLarge ? 1 : 0.75, cursor: 'pointer',
              color: isActive ? 'primary.main' : 'text.secondary',
              transition: 'color 0.15s',
              // '&:hover': { color: 'text.primary' },
              ...(wrap && isActive ? {
                borderBottom: `2px solid ${COLOR_PRIMARY}`,
                marginBottom: '-1px',
              } : {}),
            }}
          >
            {typeof tab.label === 'string' ? (
              <Typography variant="caption" sx={{
                fontSize: isLarge ? FS_SM : FS_XS,
                fontWeight: isActive ? 600 : 400,
                whiteSpace: 'nowrap',
              }}>
                {tab.label}
              </Typography>
            ) : (
              tab.label
            )}
            {tab.trailing && (
              <Box sx={{ display: 'inline-flex', ml: 0.5 }}>
                {tab.trailing}
              </Box>
            )}
          </Box>
        )
      })}

      {/* Sliding indicator - disabled when wrapping (absolute pos can't span
          multiple rows). Each active tab draws its own bottom border instead. */}
      {indicator.ready && !wrap && (
        <Box sx={{
          position: 'absolute', bottom: -1, height: 2,
          left: indicator.left, width: indicator.width,
          bgcolor: COLOR_PRIMARY,
          transition: 'left 0.18s ease, width 0.18s ease',
          pointerEvents: 'none',
        }} />
      )}

      {endSlot && (
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', pr: 0.5 }}>
          {endSlot}
        </Box>
      )}
    </Box>
  )
}
